import { el, clear, toast } from '../core/dom.js';
import { getState } from '../core/state.js';
import { recommend } from '../rescue/rescue-engine.js';
import { getModule, RESCUE_MODULES } from '../rescue/rescue-registry.js';
import { filterModules } from '../rescue/module-filter.js';
import { CATEGORY_TO_DOMAIN } from '../domain/default-methods.js';
import { consumptionMethods, logEvent, rescueStats } from '../storage/db.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { uuid } from '../core/ids.js';
import { currentRoute, navigate } from '../router.js';
import { copy } from '../copy/tone.js';
import { track } from '../analytics/ga.js';
import type { BehaviorDomain, ConsumptionMethodRecord } from '../core/types.js';

/**
 * Build a collapsible "Browse all rescue tools" section listing every module
 * not already shown in the recommended grid above. Safety filters
 * (domain exclusion, gambling-like, experimental, reduced-motion) still
 * apply — the user can reach any safe module at any time, but a gambling-
 * domain user still never sees Blackjack.
 */
export function buildBrowseAllSection(
  shownIds: ReadonlySet<string>,
  domain: BehaviorDomain | undefined,
  reducedMotion: boolean,
  onPick: (moduleId: string) => void,
): HTMLElement {
  const all = filterModules(RESCUE_MODULES, {
    ...(domain ? { domain } : {}),
    reducedMotion,
  });
  const remainder = all.filter((m) => !shownIds.has(m.id));
  const details = el('details', {
    class: 'card card-compact',
    attrs: { style: 'margin-top:var(--space-3);' },
  });
  const summary = el('summary', {}, 'Browse all rescue tools');
  summary.addEventListener('click', () => {
    if (!(details as HTMLDetailsElement).open) track('rescue_browse_all_opened');
  });
  details.appendChild(summary);
  if (remainder.length === 0) {
    details.appendChild(el('p', { class: 'muted-sm', attrs: { style: 'margin:var(--space-3) 0 0;' } },
      'Every available tool is shown above.'));
    return details;
  }
  const grid = el('div', { class: 'option-grid', attrs: { style: 'margin-top:var(--space-3);' } });
  for (const m of remainder) {
    grid.appendChild(el('button', {
      class: 'option-tile',
      attrs: { type: 'button' },
      onclick: () => onPick(m.id),
    },
      el('span', { class: 'option-tile-kind' }, kindLabel(m.kind)),
      el('span', { class: 'option-tile-name' }, m.name),
      el('span', { class: 'option-tile-meta' }, formatDurationShort(m.defaultDurationSec)),
    ));
  }
  details.appendChild(grid);
  return details;
}

function formatDurationShort(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.round(sec / 60);
  return `${m}m`;
}

function kindLabel(kind: string): string {
  if (kind === 'breathing' || kind === 'reflection') return 'Wellness';
  if (kind === 'minigame') return 'Minigame';
  if (kind === 'movement') return 'Movement';
  return kind;
}

const GROUP_ORDER: readonly string[] = ['Minigame', 'Wellness', 'Movement'];
const GROUP_PLURAL: Readonly<Record<string, string>> = {
  Wellness: 'Wellness',
  Minigame: 'Minigames',
  Movement: 'Movement',
};

interface TileLike {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly defaultDurationSec: number;
}

function renderGroupedTiles<T extends TileLike>(
  parent: HTMLElement,
  list: readonly T[],
  onPick: (id: string) => void,
): void {
  const groups = new Map<string, T[]>();
  for (const m of list) {
    const label = kindLabel(m.kind);
    const bucket = groups.get(label) ?? [];
    bucket.push(m);
    groups.set(label, bucket);
  }
  const orderedLabels = [
    ...GROUP_ORDER.filter((g) => groups.has(g)),
    ...[...groups.keys()].filter((g) => !GROUP_ORDER.includes(g)),
  ];
  for (const label of orderedLabels) {
    const items = groups.get(label) ?? [];
    if (items.length === 0) continue;
    const section = el('div', { class: 'rescue-group' });
    section.appendChild(el('h3', { class: 'rescue-group-title' },
      GROUP_PLURAL[label] ?? label));
    const grid = el('div', { class: 'option-grid' });
    for (const m of items) {
      grid.appendChild(el('button', {
        class: 'option-tile',
        attrs: { type: 'button' },
        onclick: () => onPick(m.id),
      },
        el('span', { class: 'option-tile-kind' }, kindLabel(m.kind)),
        el('span', { class: 'option-tile-name' }, m.name),
        el('span', { class: 'option-tile-meta' }, formatDurationShort(m.defaultDurationSec)),
      ));
    }
    section.appendChild(grid);
    parent.appendChild(section);
  }
}

interface RescueContext {
  habitId: string;
  method?: ConsumptionMethodRecord | undefined;
}

let activeContext: RescueContext | null = null;

async function resolveContext(): Promise<RescueContext | null> {
  const st = getState();
  if (!st.activeHabit) return null;
  const params = currentRoute().params;
  const requestedHabit = params.get('habit');
  const habitId = (requestedHabit && st.habits.find((h) => h.id === requestedHabit))
    ? requestedHabit
    : st.activeHabit.id;
  const requestedMethodId = params.get('method') ?? undefined;
  let method: ConsumptionMethodRecord | undefined;
  if (requestedMethodId) {
    const all = await consumptionMethods.byHabit(habitId);
    method = all.find((m) => m.id === requestedMethodId && m.active);
  }
  const ctx: RescueContext = { habitId };
  if (method) ctx.method = method;
  return ctx;
}

export async function renderRescue(parent: HTMLElement) {
  clear(parent);
  const st = getState();
  const noPlan = !st.activeHabit || !st.activePlan;
  if (noPlan) {
    // Emergency rescue path — render the default rescue library without a habit
    // or plan. The user may not be onboarded yet.
    await renderEmergencyRescue(parent);
    return;
  }
  const ctx = await resolveContext();
  activeContext = ctx;
  const reducedMotion = st.settings.reducedMotion === 'on'
    || (st.settings.reducedMotion === 'auto' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const habitId = ctx?.habitId ?? st.activeHabit!.id;
  const stats = await rescueStats.byHabit(habitId);
  const successByModuleId: Record<string, number> = {};
  for (const s of stats) successByModuleId[s.moduleId] = s.starts > 0 ? s.completions / s.starts : 0;
  const recent = [...stats].sort((a, b) => (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? ''))
    .slice(0, 2).map((s) => s.moduleId);

  const habit = st.habits.find((h) => h.id === habitId) ?? st.activeHabit;
  if (!habit) { parent.appendChild(el('p', {}, 'Habit not found.')); return; }
  const domain = habit.domain ?? CATEGORY_TO_DOMAIN[habit.category];
  const list = recommend({
    preferredKinds: st.settings.preferredRescueTypes,
    reducedMotion,
    durationSec: st.settings.defaultRescueDurationSec,
    triggers: st.settings.triggers,
    recentlyUsedModuleIds: recent,
    successByModuleId,
    domain,
    favoriteModuleIds: st.settings.favoriteRescueModuleIds,
  });

  const headerCard = el('div', { class: 'card' },
    el('h2', {}, 'Rescue'),
    el('p', { class: 'muted' }, 'Buy yourself a moment. Pick one.'),
  );
  if (ctx?.method) {
    headerCard.appendChild(el('p', { class: 'muted', attrs: { style: 'margin-top:0.25rem;' } },
      `Context: ${habit.label} · ${ctx.method.label}`));
  }
  parent.appendChild(headerCard);

  renderGroupedTiles(parent, list, (id) => start(parent, id));

  parent.appendChild(el('div', { class: 'card', attrs: { style: 'margin-top:1rem;' } },
    el('h2', {}, 'Delay ladder'),
    el('p', { class: 'muted' }, 'Can you wait 30 seconds? 2 minutes? Stack small wins.'),
    el('div', { class: 'row' },
      el('button', { class: 'btn', onclick: () => start(parent, 'breathing-orb', 30) }, '30 sec'),
      el('button', { class: 'btn', onclick: () => start(parent, 'breathing-orb', 60) }, '1 min'),
      el('button', { class: 'btn', onclick: () => start(parent, 'breathing-orb', 120) }, '2 min'),
    ),
  ));
}

async function start(parent: HTMLElement, moduleId: string, overrideSec?: number) {
  const mod = getModule(moduleId);
  if (!mod) { toast('Module not found.'); return; }
  const st = getState();
  const reducedMotion = st.settings.reducedMotion === 'on'
    || (st.settings.reducedMotion === 'auto' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const sessionId = uuid();
  const startAt = new Date();
  const methodPayload = activeContext?.method
    ? { methodId: activeContext.method.id, methodKind: activeContext.method.kind }
    : {};
  await logEvent({
    type: 'rescue_started',
    habitId: activeContext?.habitId ?? st.activeHabit?.id,
    planId: st.activePlan?.id,
    occurredAt: startAt.toISOString(),
    localDate: localDate(startAt),
    timezoneOffsetMinutes: tzOffsetMinutes(startAt),
    payload: { sessionId, moduleId, kind: mod.kind, plannedDurationSec: overrideSec ?? mod.defaultDurationSec, ...methodPayload },
  });

  clear(parent);
  const host = el('div');
  parent.appendChild(host);

  const result = await mod.run(host, {
    durationSec: overrideSec ?? mod.defaultDurationSec,
    reducedMotion,
    motivation: st.settings.motivationAnchor,
  });

  const endAt = new Date();
  const durationMs = endAt.getTime() - startAt.getTime();
  await logEvent({
    type: result.kind === 'completed' ? 'rescue_completed' : 'rescue_abandoned',
    habitId: activeContext?.habitId ?? st.activeHabit?.id,
    planId: st.activePlan?.id,
    occurredAt: endAt.toISOString(),
    localDate: localDate(endAt),
    timezoneOffsetMinutes: tzOffsetMinutes(endAt),
    payload: { sessionId, moduleId, kind: mod.kind, durationMs, ...methodPayload, ...result.details },
  });

  // Update per-habit rescue stats only when a habit exists. The pre-onboarding
  // emergency rescue path has no habit; that's fine — skip stats but still
  // surface the result screen so the user is not stranded on the frozen
  // module DOM.
  const habitId = activeContext?.habitId ?? st.activeHabit?.id;
  if (habitId) {
    const all = await rescueStats.byHabit(habitId);
    const cur = all.find((s) => s.moduleId === moduleId) ?? {
      id: uuid(), habitId, moduleId, kind: mod.kind,
      starts: 0, completions: 0, abandonments: 0,
      completedThenWaited: 0, completedThenUsed: 0,
      averageDurationMs: 0, updatedAt: nowIso(),
    };
    cur.starts += 1;
    if (result.kind === 'completed') cur.completions += 1; else cur.abandonments += 1;
    cur.averageDurationMs = Math.round((cur.averageDurationMs * (cur.starts - 1) + durationMs) / cur.starts);
    cur.lastUsedAt = endAt.toISOString();
    cur.updatedAt = nowIso();
    await rescueStats.put(cur);
  }

  // Skip the result screen if the user navigated away during the (possibly
  // multi-minute) module run. Otherwise we'd clobber whatever they're now
  // looking at with a "You stayed with it." card minutes later.
  if (location.hash.startsWith('#/rescue')) {
    await renderResultScreen(parent, result.kind, sessionId);
  }
}

async function renderResultScreen(parent: HTMLElement, kind: 'completed' | 'abandoned', sessionId: string) {
  clear(parent);
  const st = getState();
  const tone = st.settings.tone;
  const hasPlan = st.plans.some((p) => p.active);
  parent.appendChild(el('div', { class: 'card center' },
    el('h2', {}, kind === 'completed' ? 'You stayed with it.' : 'No problem.'),
    el('p', { class: 'muted' }, copy(kind === 'completed' ? 'rescue_complete' : 'rescue_abandon', tone)),
  ));
  if (!hasPlan) {
    // Pre-onboarding emergency path: nudge into the quick-start flow instead of /today.
    parent.appendChild(el('div', { class: 'stack' },
      el('button', { class: 'btn primary block', onclick: () => navigate('/onboard') }, 'Set up a quick plan'),
      el('button', { class: 'btn block', onclick: () => navigate('/rescue') }, 'Try another rescue'),
    ));
    return;
  }
  parent.appendChild(el('div', { class: 'stack' },
    el('button', { class: 'btn primary block', onclick: () => navigate('/today') }, 'Keep waiting'),
    el('button', { class: 'btn block', onclick: () => navigate('/rescue') }, 'Try another rescue'),
    el('button', { class: 'btn block', onclick: () => quickLogUsed(sessionId) }, 'Log my use'),
  ));
}

async function quickLogUsed(_sessionId: string) {
  // Route to Now where the user's domain quick-log buttons live. The full
  // "I waited X before using" linkage happens through the Urge Command Center
  // path; from the rescue library we don't have an active episode to bind to.
  navigate('/today');
}

async function renderEmergencyRescue(parent: HTMLElement): Promise<void> {
  const st = getState();
  const reducedMotion = st.settings.reducedMotion === 'on'
    || (st.settings.reducedMotion === 'auto' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const list = recommend({
    preferredKinds: st.settings.preferredRescueTypes,
    reducedMotion,
    durationSec: st.settings.defaultRescueDurationSec,
  });
  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Rescue'),
    el('p', { class: 'muted' }, 'Pick one. You can finish setting up your plan after.'),
  ));
  renderGroupedTiles(parent, list, (id) => start(parent, id));
  parent.appendChild(el('div', { class: 'stack', attrs: { style: 'margin-top:1rem;' } },
    el('button', { class: 'btn block', onclick: () => navigate('/onboard') }, 'Set up my plan'),
    el('button', {
      class: 'btn ghost block',
      // No plan exists, so /today falls back to the welcome screen via
      // app-shell's onboarded check. Sends the user to the calmer landing
      // rather than the rescue picker they just came from.
      onclick: () => navigate('/today'),
    }, 'Maybe later'),
  ));
}
